/**
 * Pure helper functions shared across useCampaignForm sub-hooks.
 * Extracted from the original monolithic useCampaignForm.ts.
 *
 * Naming convention: all functions use the `get*` prefix to denote
 * data-retrieval/derivation (as opposed to mutation).
 */

import { MerchantGroupBehaviour } from 'src/@types/valueMatching/enums';
import { MerchantCPGNDetailItem } from 'src/@types/valueMatching/campaignConfiguration';

/**
 * Returns the default merchant group name from the given merchant list.
 * Pre-selects the merchant whose UI behaviour is set to {@link MerchantGroupBehaviour.DEFAULT}.
 * Falls back to the first merchant in the list, or an empty string if the list is empty.
 */
export function getDefaultMerchantGroup(merchants: MerchantCPGNDetailItem[]): string {
    const defaultMerchant = merchants.find(
        (merchant) => merchant.merchantGroupUIHints?.merchantGroupBehaviour === MerchantGroupBehaviour.DEFAULT,
    );
    return defaultMerchant?.merchantGroup ?? (merchants.length > 0 ? merchants[0].merchantGroup : '');
}

/**
 * Returns the CPGN (Campaign) accounts associated with a given merchant group.
 * If the merchant group is not found, returns an empty array.
 */
export function getCpgnAccountsForMerchant(
    merchants: MerchantCPGNDetailItem[],
    merchantGroup: string,
): string[] {
    const matchedMerchant = merchants.find((merchant) => merchant.merchantGroup === merchantGroup);
    return matchedMerchant?.cpgnAccounts ?? [];
}
